import { useState } from "react";
import {
  Car,
  DollarSign,
  ClipboardCheck,
  ArrowRight,
  Check,
} from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  Input,
  Select,
  type SelectOption,
} from "@/components/ui";
import { formatPrice } from "@/utils/format";
import { useSeo } from "@/lib/seo";

const currentYear = new Date().getFullYear();
const yearOptions: SelectOption[] = Array.from({ length: 25 }, (_, i) => ({
  value: String(currentYear - i),
  label: String(currentYear - i),
}));

const makeOptions: SelectOption[] = [
  { value: "", label: "Select Make" },
  { value: "toyota", label: "Toyota" },
  { value: "honda", label: "Honda" },
  { value: "ford", label: "Ford" },
  { value: "chevrolet", label: "Chevrolet" },
  { value: "bmw", label: "BMW" },
  { value: "mercedes-benz", label: "Mercedes-Benz" },
  { value: "audi", label: "Audi" },
  { value: "nissan", label: "Nissan" },
  { value: "hyundai", label: "Hyundai" },
  { value: "kia", label: "Kia" },
  { value: "lexus", label: "Lexus" },
  { value: "mazda", label: "Mazda" },
  { value: "subaru", label: "Subaru" },
  { value: "volkswagen", label: "Volkswagen" },
];

const conditionOptions: SelectOption[] = [
  { value: "", label: "Select Condition" },
  { value: "excellent", label: "Excellent - Like new, no issues" },
  { value: "good", label: "Good - Minor wear, fully functional" },
  { value: "fair", label: "Fair - Some cosmetic/mechanical issues" },
  { value: "poor", label: "Poor - Significant issues or damage" },
];

const steps = [
  {
    icon: Car,
    title: "Enter Vehicle Details",
    description: "Tell us about your vehicle - make, model, year, and mileage.",
  },
  {
    icon: DollarSign,
    title: "Get Instant Offer",
    description: "Receive a competitive offer based on market data.",
  },
  {
    icon: ClipboardCheck,
    title: "Complete Trade-In",
    description: "Apply your offer to your next purchase or get cash.",
  },
];

export function TradeInPage() {
  useSeo({
    title: "Trade-In Value | BESPOKIAN | Auto",
    description:
      "Get an instant offer on your current vehicle. No hassle, no lowball offers.",
    image: "/home-images/quick-actions/trade-in-value.jpg",
  });

  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    vin: "",
    year: "",
    make: "",
    model: "",
    trim: "",
    mileage: "",
    condition: "",
    hasAccidents: false,
    ownsOutright: true,
  });
  const [estimate, setEstimate] = useState<number | null>(null);

  const updateField = (field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Mock estimate calculation
    const baseValue = 25000;
    const yearAdjustment = (parseInt(formData.year) - 2015) * 500;
    const mileageAdjustment = -parseInt(formData.mileage || "0") * 0.03;
    const conditionMultiplier =
      formData.condition === "excellent"
        ? 1.1
        : formData.condition === "good"
          ? 1
          : formData.condition === "fair"
            ? 0.85
            : 0.7;
    const accidentAdjustment = formData.hasAccidents ? -2000 : 0;

    const estimatedValue = Math.max(
      1000,
      Math.round(
        (baseValue + yearAdjustment + mileageAdjustment + accidentAdjustment) *
          conditionMultiplier,
      ),
    );
    setEstimate(estimatedValue * 100); // Convert to cents
    setStep(2);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <header className="relative min-h-[70vh] flex items-start justify-center pt-28 md:pt-32 pb-16 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat bg-gray-900"
          style={{
            backgroundImage: `url('/home-images/heroes/trade-in-value.webp')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/20 to-black/60" />
        <div className="absolute inset-0 bg-black/35" />

        <div className="relative z-10 w-full">
          <div className="text-center px-6 max-w-4xl mx-auto">
            <div className="mb-8">
              <h1 className="font-display text-5xl md:text-6xl text-white mb-4 leading-tight tracking-tight">
                Trade In Your Vehicle
              </h1>
              <p className="text-white text-lg md:text-xl max-w-2xl mx-auto">
                Get an instant offer on your current vehicle. No hassle, no
                lowball offers.
              </p>
              <div className="w-24 h-px bg-white mx-auto mt-6" />
            </div>
          </div>
        </div>
      </header>

      {/* How It Works */}
      <section className="py-12 bg-white border-b">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">
            How It Works
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((stepItem, index) => (
              <div key={stepItem.title} className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                    <stepItem.icon className="h-5 w-5 text-primary-600" />
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-primary-600">
                      Step {index + 1}
                    </span>
                  </div>
                  <h3 className="font-semibold text-gray-900">
                    {stepItem.title}
                  </h3>
                  <p className="mt-1 text-sm text-gray-600">
                    {stepItem.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="max-w-2xl mx-auto">
            {step === 1 ? (
              <Card>
                <CardContent className="p-6 sm:p-8">
                  <h2 className="text-xl font-semibold text-gray-900 mb-6">
                    Tell Us About Your Vehicle
                  </h2>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <Input
                      label="VIN (Optional)"
                      placeholder="Enter 17-character VIN"
                      value={formData.vin}
                      onChange={(e) => updateField("vin", e.target.value)}
                      helperText="Enter VIN for a more accurate estimate"
                      maxLength={17}
                    />

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Select
                        label="Year"
                        options={yearOptions}
                        value={formData.year}
                        onChange={(e) => updateField("year", e.target.value)}
                        placeholder="Select Year"
                      />
                      <Select
                        label="Make"
                        options={makeOptions}
                        value={formData.make}
                        onChange={(e) => updateField("make", e.target.value)}
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Input
                        label="Model"
                        placeholder="e.g., Camry"
                        value={formData.model}
                        onChange={(e) => updateField("model", e.target.value)}
                      />
                      <Input
                        label="Trim (Optional)"
                        placeholder="e.g., XSE"
                        value={formData.trim}
                        onChange={(e) => updateField("trim", e.target.value)}
                      />
                    </div>

                    <Input
                      label="Mileage"
                      type="number"
                      placeholder="Enter current mileage"
                      value={formData.mileage}
                      onChange={(e) => updateField("mileage", e.target.value)}
                    />

                    <Select
                      label="Condition"
                      options={conditionOptions}
                      value={formData.condition}
                      onChange={(e) => updateField("condition", e.target.value)}
                    />

                    <div className="space-y-3">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.hasAccidents}
                          onChange={(e) =>
                            updateField("hasAccidents", e.target.checked)
                          }
                          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-700">
                          Vehicle has accident history
                        </span>
                      </label>
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.ownsOutright}
                          onChange={(e) =>
                            updateField("ownsOutright", e.target.checked)
                          }
                          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-700">
                          I own this vehicle outright (no loan)
                        </span>
                      </label>
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      className="w-full"
                      disabled={
                        !formData.year ||
                        !formData.make ||
                        !formData.model ||
                        !formData.mileage ||
                        !formData.condition
                      }
                    >
                      Get My Offer
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-6 sm:p-8 text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Check className="h-8 w-8 text-green-600" />
                  </div>

                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    Your Estimated Trade-In Value
                  </h2>
                  <p className="text-gray-600 mb-6">
                    {formData.year} {formData.make} {formData.model}
                    {formData.trim && ` ${formData.trim}`}
                  </p>

                  <div className="bg-primary-50 rounded-xl p-6 mb-6">
                    <p className="text-sm text-primary-600 mb-1">
                      Estimated Value
                    </p>
                    <p className="text-4xl font-bold text-primary-700">
                      {estimate && formatPrice(estimate)}
                    </p>
                    <p className="text-sm text-primary-600 mt-2">
                      Based on {parseInt(formData.mileage).toLocaleString()}{" "}
                      miles • {formData.condition} condition
                    </p>
                  </div>

                  <div className="space-y-3">
                    <Button size="lg" className="w-full">
                      Apply to My Purchase
                    </Button>
                    <Button variant="outline" size="lg" className="w-full">
                      Schedule In-Person Appraisal
                    </Button>
                    <button
                      onClick={() => {
                        setStep(1);
                        setEstimate(null);
                      }}
                      className="text-sm text-gray-600 hover:text-gray-900"
                    >
                      Start Over
                    </button>
                  </div>

                  <p className="mt-6 text-xs text-gray-500">
                    * This is an estimated value based on the information
                    provided. Final offer may vary after in-person inspection.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
